import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CButton, CFormInput, CFormLabel, CFormTextarea, CMultiSelect } from "@coreui/react-pro";
import VisibilitySensor from "react-visibility-sensor";
import { api } from "../../../../config/api";
import { trackLead } from "../../../../utils/tracking";
import bgImage from "../../../../landingPages/assets/img/landingPageOne/sectionSix/intermodal-truck-bg.jpeg";
import bone from "../../../assets/img/landingPageOne/sectionSix/bone.png"
import logo1 from "../../../assets/img/landingPageOne/sectionSix/logo 1.png"
import logo2 from "../../../assets/img/landingPageOne/sectionSix/logo2.png"

const PERMISSION_COUNTRIES = [
  "Argentina",
  "Uruguai",
  "Chile",
  "Paraguai",
  "Bolívia",
  "Peru",
  "Colômbia",
];

const DEFAULT_FORM_DATA = {
  Name: "",
  Phone: "",
  VehicleType: "",
  Permission: [],
  isLoading: false,
  error: null,
};

function maskPhone(value) {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function MeatSkewer({ width = 80, height = 90 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 80 90" fill="none">
      <g transform="rotate(-28 40 45)">
        <rect x="4" y="43" width="66" height="4" rx="2" fill="#D9A25C"/>
        <path d="M70 39 L80 45 L70 51 Z" fill="#D9A25C"/>
        <rect x="9" y="35" width="19" height="19" rx="5" fill="#7A3210"/>
        <rect x="31" y="35" width="19" height="19" rx="5" fill="#8B3A1A"/>
        <rect x="53" y="35" width="19" height="19" rx="5" fill="#7A3210"/>
        <ellipse cx="14" cy="40" rx="5" ry="4" fill="#fff" opacity="0.18"/>
        <ellipse cx="36" cy="40" rx="5" ry="4" fill="#fff" opacity="0.18"/>
        <ellipse cx="58" cy="40" rx="5" ry="4" fill="#fff" opacity="0.18"/>
        <path d="M13 40 L24 51 M22 39 L13 48" stroke="#4A1E0A" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M35 40 L46 51 M44 39 L35 48" stroke="#4A1E0A" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M57 40 L68 51 M66 39 L57 48" stroke="#4A1E0A" strokeWidth="1.6" strokeLinecap="round"/>
      </g>
    </svg>
  );
}

function BeerToastAnimation({ name, onDone }) {
  return (
    <div className="beerToastOverlay">
      <div className="beerToastContent">
        <div className="beerGlasses">
          <span className="beerLeft"><MeatSkewer width={70} height={78} /></span>
          <span className="beerSparkle">✨</span>
          <span className="beerRight"><MeatSkewer width={70} height={78} /></span>
        </div>
        <h2 className="beerToastTitle">Parabéns, {name}! 🎉</h2>
        <p className="beerToastMsg">Seu cadastro foi confirmado!</p>
        <button className="beerToastBtn" onClick={onDone}>
          Ver meu vale-churrasco
        </button>
      </div>
    </div>
  );
}

function SectionSix() {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [qrCodeBase64, setQrCodeBase64] = useState(null);
  const [ticketCode, setTicketCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [permissionResetKey, setPermissionResetKey] = useState(0);
  const { t } = useTranslation();
  const [submittedName, setSubmittedName] = useState("");
  const onChange = (visibility) => {
    if (visibility) setIsVisible(true);
  };

  const isFormValid = () =>
    formData.Name.trim().length >= 3 &&
    formData.Phone.replace(/\D/g, "").length >= 10 &&
    formData.VehicleType.trim().length >= 2 &&
    formData.Permission.length > 0;

  // Opções estáveis: o CMultiSelect gerencia a seleção internamente a partir do
  // "selected" inicial, então recalcular esse array a cada render (a partir do
  // formData) quebra a sincronização interna dele ao remover uma tag.
  const permissionOptions = useMemo(
    () => PERMISSION_COUNTRIES.map((country) => ({ value: country, text: country })),
    []
  );

  const handlePermissionChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      Permission: selectedOptions.map((option) => option.text),
      error: null,
    }));
  };

  const handleSubmit = async () => {
    if (formData.isLoading) return;
    if (!isFormValid()) return;

    setFormData((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const qrRes = await api.post("registerQrCode", {
        Name: formData.Name,
        Phone: formData.Phone,
        Vehicle_type: formData.VehicleType,
        Permission: formData.Permission.join(", "),
      });

      const qrImage = qrRes.data?.data?.qrCodeImage;
      const ticket = qrRes.data?.data?.ticket;

      if (!qrImage || !ticket) {
        const isConnectionError = !qrRes.response && !qrRes.data;
        throw new Error(
          qrRes.response?.data?.message ||
          (isConnectionError
            ? "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente."
            : "Erro ao gerar QR Code")
        );
      }

      // salva nome antes de limpar o form
      setSubmittedName(formData.Name);

      // 🔥 TRACKING (GTM)
      trackLead("lp_intermodal_chopp", {
        event_type: "qr_generated",
        vehicle_type: formData.VehicleType,
        permission: formData.Permission.join(", "),
      });

      // 🔥 TRACKING (Meta Pixel)
      window.fbq?.("track", "Lead", {
        content_name: "intermodal_event",
        content_category: "evento_presencial",
      });

      // UI
      setQrCodeBase64(qrImage);
      setTicketCode(ticket);
      setShowToast(true);
      setFormData(DEFAULT_FORM_DATA);
      setPermissionResetKey((key) => key + 1);

    } catch (err) {
      console.error("Erro ao registrar QR Code:", err);

      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Não foi possível gerar seu QR Code. Tente novamente.";

      setFormData((prev) => ({
        ...prev,
        error: errorMessage,
      }));
    } finally {
      setFormData((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(ticketCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = qrCodeBase64;
    link.download = `ticket-intermodal2026-${submittedName
    .replace(/\s+/g, "-")
    .toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const pin = (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="var(--cor-secondary)"/>
    </svg>
  )

  return (
    <VisibilitySensor onChange={onChange} partialVisibility minTopValue={100}>
      <div
        id="register"
        className={`sectionSix ${isVisible ? "visible" : ""}`}
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        {showToast && (
          <BeerToastAnimation name={submittedName}  onDone={() => setShowToast(false)}/>
        )}
        <div className="sectionSixText">
          <img src={logo1} alt="" width={'180px'} style={{marginBottom: '15px'}}/>
          <h1 className={isVisible ? "visible" : "  "}>
            <span className="highlight">Seu próximo caminho pode ser com a GH!</span>
          </h1>
          <p style={{
              backgroundColor: 'var(--cor-secondary)',
              width: 'fit-content',
              padding: '5px 25px 2px',
              borderRadius: '15px',
              color: 'var(--cor-white)',
              fontWeight: '600',
              fontSize: '16px',
              margin: '20px 0px 10px 0px'
            }}
          >
            Dia 16/09 - Cadastre-se e participe
          </p>
          <p>
            Os <span className="highlight-text">100</span> primeiros ganham um <span className="highlight-text">boné GH</span>
            <br />
            Ganhe um <span className="highlight-text">vale-churrasco</span> durante a ação
            <br />
             Concorra a um <span className="highlight-text">Kit Mate GH</span> no final do evento
          </p>
          <a
            href="https://maps.app.goo.gl/xfabRZSHtd62Q7Sq7"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: 'var(--cor-white)',
              width: 'fit-content',
              height: 'fit-content',
              padding: '2px 30px 2px 5px',
              borderRadius: '15px',
              color: 'var(--cor-tertiary)',
              fontWeight: '600',
              fontSize: '16px',
              margin: '0px 0px 5px',
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            {pin}
            <span style={{marginTop: '2px'}}>R. Gen. San Martín, 3700 - Uruguaiana - RS</span>
          </a>
          
        </div>
        <img className="bone" src={bone} alt="" width={'300px'}/>
        {!qrCodeBase64 || showToast ? (
          <div id="qrcodeForm" className={`registerForm ${isVisible ? "visible" : ""}`}>
            <div>
              <CFormLabel>{t("lp1_register_name")}</CFormLabel>
              <CFormInput
                type="text"
                maxLength={80}
                minLength={3}
                value={formData.Name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, Name: e.target.value, error: null }))
                }
                placeholder=""
              />
              <CFormLabel>{t("lp1_register_phone")}</CFormLabel>
              <CFormInput
                type="tel"
                autoComplete="off"
                maxLength={15}
                value={formData.Phone}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    Phone: maskPhone(e.target.value),
                    error: null,
                  }))
                }
                placeholder="(00) 00000-0000"
              />
              <CFormLabel>{t("lp1_register_vehicle")}</CFormLabel>
              <CFormTextarea
                rows={3}
                autoComplete="off"
                maxLength={255}
                value={formData.VehicleType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    VehicleType: e.target.value,
                    error: null,
                  }))
                }
                placeholder=""
              />
              <CFormLabel>{t("lp1_register_permission")}</CFormLabel>
              <CMultiSelect
                key={permissionResetKey}
                className="permissionMultiSelect"
                options={permissionOptions}
                onChange={handlePermissionChange}
                optionsStyle="checkbox"
                selectionType="tags"
                cleaner={false}
                placeholder={t("lp1_register_permission_placeholder")}
                selectAllLabel={t("lp1_register_permission_select_all")}
              />

              {formData.error && (
                <p className="registerError">{formData.error}</p>
              )}

              <CButton
                className="mt-4 registerSubmitBtn"
                onClick={handleSubmit}
                disabled={formData.isLoading || !isFormValid()}
              >
                {formData.isLoading
                  ? t("lp1_register_sending")
                  : t("lp1_register_send")}
              </CButton>
            </div>
          </div>
        ) : (
          <div className={`ticketCard ${isVisible ? "visible" : ""}`}>
            <h2>{t("lp1_register_ticket_title")}</h2>
            <p className="ticketName">{submittedName}</p>
            <div className="qrCodeWrapper">
              <img
                src={qrCodeBase64}
                alt="QR Code do ticket"
                className="qrCodeImg"
              />
            </div>
            {ticketCode && (
              <div
                className="ticketCodeBox"
                onClick={handleCopy}
                title="Clique para copiar"
              >
                <span className="ticketCodeLabel">
                  {t("lp1_register_ticket_id")}
                </span>
                <code className="ticketCodeValue">{ticketCode}</code>
                <span className="ticketCopyHint">
                  {copied ? "✅ Copiado!" : "📋 Clique para copiar"}
                </span>
              </div>
            )}
            <p className="ticketInstruction">
              {t("lp1_register_ticket_instruction")}
            </p>
            <button className="downloadBtn" onClick={handleDownload}>
              ⬇ Baixar QR Code
            </button>
          </div>
        )}
      </div>
    </VisibilitySensor>
  );
}

export default SectionSix;
